<template>
  <v-dialog v-model="isOpenPermissionEditor" width="400" max-width="100vw" persistent>
    <v-card>
      <v-card-title class="headline pa-4">
        {{ title }}
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4 overflow-y-auto" style="max-height: calc(100vh - 210px);">
        <v-row dense>
          <v-col cols="12">
            <v-text-field
              v-model="payload.name"
              v-validate="'required'"
              :label="$t('permissions.fields.name')"
              :data-vv-name="$t('permissions.fields.name')"
              :error-messages="[...errors.collect($t('permissions.fields.name')), ...(errorMessages.name || [])]"
              :readonly="loading || ['detail'].includes(permissionEditorMode)"
              @input="errorMessages.name = []"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="payload.slug"
              v-validate="'required'"
              :label="$t('permissions.fields.slug')"
              :data-vv-name="$t('permissions.fields.slug')"
              :error-messages="[...errors.collect($t('permissions.fields.slug')), ...(errorMessages.slug || [])]"
              :readonly="loading || ['detail'].includes(permissionEditorMode)"
              @input="errorMessages.slug = []"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-select
              v-model="payload.section_id"
              :items="sections"
              :label="$t('permissions.fields.section_id')"
              :data-vv-name="$t('permissions.fields.section_id')"
              item-text="name"
              item-value="id"
              :error-messages="[...errors.collect($t('permissions.fields.section_id')), ...(errorMessages.section_id || [])]"
              :readonly="loading || ['detail'].includes(permissionEditorMode)"
              @input="errorMessages.section_id = []"
            ></v-select>
          </v-col>
          <v-col cols="12">
            <v-select
              v-model="payload.action_id"
              :items="actions"
              :label="$t('permissions.fields.action_id')"
              :data-vv-name="$t('permissions.fields.action_id')"
              item-text="name"
              item-value="id"
              :error-messages="[...errors.collect($t('permissions.fields.action_id')), ...(errorMessages.action_id || [])]"
              :readonly="loading || ['detail'].includes(permissionEditorMode)"
              @input="errorMessages.action_id = []"
            ></v-select>
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="payload.description"
              v-validate="'max:250'"
              :label="$t('permissions.fields.description')"
              :data-vv-name="$t('permissions.fields.description')"
              :error-messages="[...errors.collect($t('permissions.fields.description')), ...(errorMessages.description || [])]"
              :readonly="loading || ['detail'].includes(permissionEditorMode)"
              counter="250"
              no-resize
              rows="3"
              @input="errorMessages.description = []"
            ></v-textarea>
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="payload.is_admin"
              :label="$t('permissions.fields.is_admin')"
              :readonly="loading || ['detail'].includes(permissionEditorMode)"
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          @click="close()"
          :disabled="loading"
          color="primary"
          text
          width="120"
        >
          {{ $t('actions.cancel') }}
        </v-btn>
        <v-btn
          v-if="['detail'].includes(permissionEditorMode)"
          @click="setPermissionEditorMode('edit')"
          :loading="loading"
          color="primary"
          width="120"
        >
          {{ $t('actions.edit') }}
        </v-btn>
        <v-btn
          v-else
          @click="submit()"
          :loading="loading"
          color="primary"
          width="120"
        >
          {{ $t('actions.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'PermissionEditor',
  data: () => ({
    loading: false,
    errorMessages: {},
    payload: {
      id: null,
      action_id: null,
      section_id: null,
      name: '',
      slug: '',
      description: '',
      is_admin: false
    },
  }),
  computed: {
    ...mapGetters({
      isOpenPermissionEditor: 'permissions/isOpenPermissionEditor',
      permissionEditorMode: 'permissions/permissionEditorMode',
      permission: 'permissions/permission',
      sections: 'sections/sections',
      actions: 'actions/actions'
    }),
    title () {
      if (this.permissionEditorMode === 'create') {
        return 'Crear permiso'
      }
      if (this.permissionEditorMode === 'detail') {
        return 'Detalle de permiso'
      }
      return 'Editar permiso'
    }
  },
  watch: {
    isOpenPermissionEditor (newVal) {
      if (newVal) {
        this.loadDefault()
      }
    },
    permission (newVal) {
      if (newVal) {
        this.setPayload(newVal)
      }
    }
  },
  created () {
    this.loadDefault()
  },
  destroyed () {
    this.resetActionState()
  },
  methods: {
    ...mapActions({
      resetActionState: 'actions/resetState',
      getSections: 'sections/getSections',
      getActions: 'actions/getActions',
      savePermission: 'permissions/savePermission',
      updatePermission: 'permissions/updatePermission'
    }),
    ...mapMutations({
      togglePermissionEditor: 'permissions/togglePermissionEditor',
      setPermission: 'permissions/setPermission',
      setPermissionEditorMode: 'permissions/setPermissionEditorMode'
    }),
    loadDefault () {
      this.getActions({ fields: 'id,name', limit: 'all', sort: 'id:asc' })
      this.getSections({ fields: 'id,name', limit: 'all', sort: 'name:asc' })
    },
    async submit () {
      const isValid = await this.$validator.validateAll()
      if (!isValid) { return }
      const {
        payload,
        permissionEditorMode,
        savePermission,
        updatePermission
      } = this
      this.loading = true
      let result
      if (['create'].includes(permissionEditorMode)) {
        result = await savePermission(payload)
      } else {
        result = await updatePermission(payload)
      }
      this.loading = false
      if (result.success) {
        this.close()
      } else {
        this.setErrorMessages(result.errors)
      }
    },
    setErrorMessages (errors) {
      errors.forEach(({ field, message, validation }) => {
        if (!this.errorMessages[field]) {
          this.errorMessages[field] = []
        }
        this.errorMessages[field].push(this.$t(`messages.validation_error.${validation}`, null, { field: this.$t(`permissions.fields.${field}`) }))
      })
    },
    setPayload (payload) {
      this.payload = payload
    },
    setDefaultData () {
      this.loading = false
      this.errorMessages = {}
      this.payload = {
        id: null,
        action_id: null,
        section_id: null,
        name: '',
        slug: '',
        description: '',
        is_admin: false
      }
    },
    cleanData () {
      this.setDefaultData()
      this.setPermission()
      this.$validator.reset()
    },
    close () {
      this.togglePermissionEditor()
      this.cleanData()
    }
  }
}
</script>
